const chapter = {
  id: 'definite-integration-areas',
  slug: 'definite-integration-areas',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Definite Integration & Areas',
        hindi: 'निश्चित समाकलन और क्षेत्रफल',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main, 1–2 in Advanced',
        hindi: 'जेईई मेन में 2-3 प्रश्न, एडवांस्ड में 1-2 प्रश्न',
  },
  syllabusTags: ['Calculus', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'ftc',
      title: {
        english: 'Definite Integral & FTC',
        hindi: 'निश्चित समाकल और मूल प्रमेय',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Fundamental Theorem of Calculus',
        hindi: 'कैलकुलस का मौलिक प्रमेय',
          },
          statement: {
            english: `**Part 1**: If $f$ is continuous on $[a, b]$ and $F(x) = \\int_a^x f(t)\\,dt$, then $F'(x) = f(x)$.

**Part 2**: If $f$ is continuous on $[a, b]$ and $F$ is any antiderivative of $f$, then
$$\\int_a^b f(x)\\,dx = F(b) - F(a).$$`,
            hindi: '**भाग 1**: यदि $f$ $[a, b]$ और $F(x) = \\int_a^x f(t)\\,dt$ पर निरंतर है, तो $F\\\'(x) = f(x)$। **भाग 2**: यदि $f$ $[a, b]$ पर निरंतर है और $F$ $f$ का कोई प्रतिव्युत्पन्न है, तो $$\\int_a^b f(x)\\,dx = F(b) - F(a).$$',
          },
        },
        {
          type: 'figure',
          svg: `
<svg viewBox="0 0 380 230" xmlns="http://www.w3.org/2000/svg">
  <line x1="20" y1="180" x2="360" y2="180" stroke="#374151" stroke-width="1.2"/>
  <line x1="60" y1="20" x2="60" y2="220" stroke="#374151" stroke-width="1.2"/>
  <polygon points="90,180 90,180.0 93,177.1 96,174.3 99,171.7 102,169.2 105,166.9 108,164.7 111,162.7 114,160.8 117,159.1 120,157.5 123,156.1 126,154.8 129,153.7 132,152.7 135,151.9 138,151.2 141,150.7 144,150.3 147,150.1 150,150.0 153,150.1 156,150.3 159,150.7 162,151.2 165,151.9 168,152.7 171,153.7 174,154.8 177,156.1 180,157.5 183,159.1 186,160.8 189,162.7 192,164.7 195,166.9 198,169.2 201,171.7 204,174.3 207,177.1 210,180.0 210,180" fill="#a5b4fc" fill-opacity="0.5" stroke="none"/>
  <polyline points="90,180.0 93,177.1 96,174.3 99,171.7 102,169.2 105,166.9 108,164.7 111,162.7 114,160.8 117,159.1 120,157.5 123,156.1 126,154.8 129,153.7 132,152.7 135,151.9 138,151.2 141,150.7 144,150.3 147,150.1 150,150.0 153,150.1 156,150.3 159,150.7 162,151.2 165,151.9 168,152.7 171,153.7 174,154.8 177,156.1 180,157.5 183,159.1 186,160.8 189,162.7 192,164.7 195,166.9 198,169.2 201,171.7 204,174.3 207,177.1 210,180.0" fill="none" stroke="#4338ca" stroke-width="2.2"/>
  <text x="86" y="194" font-size="12" fill="#1f2937">a</text>
  <text x="206" y="194" font-size="12" fill="#1f2937">b</text>
  <text x="120" y="150" font-size="12" fill="#3730a3">∫ₐᵇ f(x) dx</text>
  <text x="190" y="215" font-size="12" fill="#6b7280" text-anchor="middle">Definite integral = signed area between curve and x-axis on [a, b]</text>
</svg>`,
          caption: {
            english: 'The definite integral as the signed area under the curve y = f(x) between x = a and x = b.',
        hindi: 'वक्र y = f(x) between x = a and x = b के अंतर्गत हस्ताक्षरित क्षेत्र के रूप में निश्चित अभिन्न अंग।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Leibniz rule (variable limits)',
        hindi: 'लीबनिज़ नियम (परिवर्तनीय सीमाएँ)',
          },
          statement: {
            english: '$\\dfrac{d}{dx} \\int_{u(x)}^{v(x)} f(t)\\,dt = f(v(x)) v\'(x) - f(u(x)) u\'(x)$.',
        hindi: '$\\dfrac{d}{dx} \\int_{u(x)}^{v(x)} f(t)\\,dt = f(v(x)) v\'(x) - f(u(x)) u\'(x)$.',
          },
        },
      ],
    },
    {
      id: 'properties',
      title: {
        english: 'Properties of Definite Integrals',
        hindi: 'निश्चित समाकलों के गुणधर्म',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$\\int_a^b f(x)\\,dx = -\\int_b^a f(x)\\,dx$.',
        hindi: '$\\int_a^b f(x)\\,dx = -\\int_b^a f(x)\\,dx$.',
            },
            {
              english: '$\\int_a^a f(x)\\,dx = 0$.',
        hindi: '$\\int_a^a f(x)\\,dx = 0$.',
            },
            {
              english: '$\\int_a^b f(x)\\,dx = \\int_a^c f(x)\\,dx + \\int_c^b f(x)\\,dx$.',
        hindi: '$\\int_a^b f(x)\\,dx = \\int_a^c f(x)\\,dx + \\int_c^b f(x)\\,dx$.',
            },
            {
              english: '$\\int_a^b f(x)\\,dx = \\int_a^b f(a + b - x)\\,dx$ (king\'s rule).',
        hindi: '$\\int_a^b f(x)\\,dx = \\int_a^b f(a + b - x)\\,dx$ (राजा का शासन)।',
            },
            {
              english: '$\\int_0^a f(x)\\,dx = \\int_0^a f(a - x)\\,dx$.',
        hindi: '$\\int_0^a f(x)\\,dx = \\int_0^a f(a - x)\\,dx$.',
            },
            {
              english: '$\\int_{-a}^{a} f(x)\\,dx = \\begin{cases} 2 \\int_0^a f(x)\\,dx & \\text{if } f \\text{ is even} \\\\ 0 & \\text{if } f \\text{ is odd} \\end{cases}$.',
        hindi: '$\\int_{-a}^{a} f(x)\\,dx = \\begin{cases} 2 \\int_0^a f(x)\\,dx & \\text{if } f \\text{ is even} \\\\ 0 & \\text{if } f \\text{ is odd} \\end{cases}$.',
            },
            {
              english: '$\\int_0^{2a} f(x)\\,dx = \\begin{cases} 2 \\int_0^a f(x)\\,dx & \\text{if } f(2a - x) = f(x) \\\\ 0 & \\text{if } f(2a - x) = -f(x) \\end{cases}$.',
        hindi: '$\\int_0^{2a} f(x)\\,dx = \\begin{cases} 2 \\int_0^a f(x)\\,dx & \\text{if } f(2a - x) = f(x) \\\\ 0 & \\text{if } f(2a - x) = -f(x) \\end{cases}$.',
            },
            {
              english: 'If $f$ is periodic with period $T$: $\\int_0^{nT} f(x)\\,dx = n \\int_0^T f(x)\\,dx$.',
        hindi: 'यदि $f$ अवधि $T$ के साथ आवर्ती है: $\\int_0^{nT} f(x)\\,dx = n \\int_0^T f(x)\\,dx$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Evaluate $\\displaystyle \\int_0^{\\pi/2} \\dfrac{\\sin x}{\\sin x + \\cos x}\\,dx$.',
        hindi: '$\\displaystyle \\int_0^{\\pi/2} \\dfrac{\\sin x}{\\sin x + \\cos x}\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Apply king\'s rule: $I = \\int_0^{\\pi/2} \\dfrac{\\sin(\\pi/2 - x)}{\\sin(\\pi/2 - x) + \\cos(\\pi/2 - x)}\\,dx = \\int_0^{\\pi/2} \\dfrac{\\cos x}{\\cos x + \\sin x}\\,dx$. Add: $2I = \\int_0^{\\pi/2} 1\\,dx = \\pi/2 \\Rightarrow I = \\pi/4$.',
        hindi: 'राजा का नियम लागू करें: $I = \\int_0^{\\pi/2} \\dfrac{\\sin(\\pi/2 - x)}{\\sin(\\pi/2 - x) + \\cos(\\pi/2 - x)}\\,dx = \\int_0^{\\pi/2} \\dfrac{\\cos x}{\\cos x + \\sin x}\\,dx$. जोड़ें: $2I = \\int_0^{\\pi/2} 1\\,dx = \\pi/2 \\Rightarrow I = \\pi/4$.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Evaluate $\\displaystyle \\int_{-1}^{1} (x^3 + x \\cos x + \\tan^5 x)\\,dx$.',
        hindi: '$\\displaystyle \\int_{-1}^{1} (x^3 + x \\cos x + \\tan^5 x)\\,dx$ का मूल्यांकन करें.',
          },
          solution: {
            english: 'Integrand is odd in each term ⇒ integral is $0$.',
        hindi: 'इंटीग्रैंड प्रत्येक पद में विषम है ⇒ इंटीग्रल $0$ है।',
          },
        },
      ],
    },
    {
      id: 'walls-reduction',
      title: {
        english: 'Wallis & Reduction Formulae',
        hindi: 'वॉलिस और अवकरण सूत्र',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Wallis\'s formulae',
        hindi: 'वालिस के सूत्र',
          },
          statement: {
            english: '$\\displaystyle \\int_0^{\\pi/2} \\sin^n x\\,dx = \\int_0^{\\pi/2} \\cos^n x\\,dx = \\begin{cases} \\dfrac{(n-1)(n-3)\\cdots 1}{n(n-2)\\cdots 2} \\cdot \\dfrac{\\pi}{2} & n \\text{ even} \\\\[6pt] \\dfrac{(n-1)(n-3)\\cdots 2}{n(n-2)\\cdots 1} & n \\text{ odd} \\end{cases}$.',
        hindi: '$\\displaystyle \\int_0^{\\pi/2} \\sin^n x\\,dx = \\int_0^{\\pi/2} \\cos^n x\\,dx = \\begin{cases} \\dfrac{(n-1)(n-3)\\cdots 1}{n(n-2)\\cdots 2} \\cdot \\dfrac{\\pi}{2} & n \\text{ even} \\\\[6pt] \\dfrac{(n-1)(n-3)\\cdots 2}{n(n-2)\\cdots 1} & n \\text{ odd} \\end{cases}$.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Combined formula',
        hindi: 'संयुक्त सूत्र',
          },
          statement: {
            english: '$\\displaystyle \\int_0^{\\pi/2} \\sin^m x \\cos^n x\\,dx = \\dfrac{\\Gamma\\left(\\tfrac{m+1}{2}\\right)\\Gamma\\left(\\tfrac{n+1}{2}\\right)}{2\\,\\Gamma\\left(\\tfrac{m+n+2}{2}\\right)}$. For integer $m, n$ this reduces to a Wallis-style product.',
        hindi: '$\\displaystyle \\int_0^{\\pi/2} \\sin^m x \\cos^n x\\,dx = \\dfrac{\\Gamma\\left(\\tfrac{m+1}{2}\\right)\\Gamma\\left(\\tfrac{n+1}{2}\\right)}{2\\,\\Gamma\\left(\\tfrac{m+n+2}{2}\\right)}$. पूर्णांक $m, n$ के लिए यह वालिस-शैली उत्पाद में बदल जाता है।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: '$\\displaystyle \\int_0^{\\pi/2} \\sin^6 x\\,dx = ?$',
        hindi: '$\\displaystyle \\int_0^{\\pi/2} \\sin^6 x\\,dx =?$',
          },
          solution: {
            english: '$= \\dfrac{5 \\cdot 3 \\cdot 1}{6 \\cdot 4 \\cdot 2} \\cdot \\dfrac{\\pi}{2} = \\dfrac{15}{48} \\cdot \\dfrac{\\pi}{2} = \\dfrac{5\\pi}{32}$.',
        hindi: '$= \\dfrac{5 \\cdot 3 \\cdot 1}{6 \\cdot 4 \\cdot 2} \\cdot \\dfrac{\\pi}{2} = \\dfrac{15}{48} \\cdot \\dfrac{\\pi}{2} = \\dfrac{5\\pi}{32}$.',
          },
        },
      ],
    },
    {
      id: 'inequalities',
      title: {
        english: 'Estimation & Inequalities',
        hindi: 'आकलन और असमिकाएँ',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Standard inequalities',
        hindi: 'मानक असमानताएँ',
          },
          statement: {
            english: `• If $f \\le g$ on $[a, b]$ then $\\int_a^b f \\le \\int_a^b g$.
• $\\left|\\int_a^b f(x)\\,dx\\right| \\le \\int_a^b |f(x)|\\,dx$.
• If $m \\le f(x) \\le M$ on $[a, b]$, then $m(b-a) \\le \\int_a^b f \\le M(b-a)$.`,
            hindi: 'यदि $f \\le g$ $[a, b]$ पर है तो $\\int_a^b f \\le \\int_a^b g$। • $\\left|\\int_a^b f(x)\\,dx\\right| \\le \\int_a^b |f(x)|\\,dx$. • यदि $m \\le f(x) \\le M$ को $[a, b]$ पर, तो $m(b-a) \\le \\int_a^b f \\le M(b-a)$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Mean Value Theorem for Integrals',
        hindi: 'इंटीग्रल्स के लिए माध्य मान प्रमेय',
          },
          statement: {
            english: 'If $f$ is continuous on $[a, b]$, there exists $c \\in (a, b)$ with $\\int_a^b f(x)\\,dx = f(c)(b - a)$.',
        hindi: 'यदि $f$ $[a, b]$ पर निरंतर है, तो $\\int_a^b f(x)\\,dx = f(c)(b - a)$ के साथ $c \\in (a, b)$ मौजूद है।',
          },
        },
      ],
    },
    {
      id: 'limit-as-sum',
      title: {
        english: 'Definite Integral as Limit of a Sum',
        hindi: 'योग की सीमा के रूप में निश्चित समाकल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Riemann sum',
        hindi: 'रीमैन योग',
          },
          statement: {
            english: '$\\displaystyle \\int_a^b f(x)\\,dx = \\lim_{n \\to \\infty} \\sum_{r = 1}^{n} f\\left(a + r \\cdot \\dfrac{b-a}{n}\\right) \\cdot \\dfrac{b-a}{n}$.',
        hindi: '$\\displaystyle \\int_a^b f(x)\\,dx = \\lim_{n \\to \\infty} \\sum_{r = 1}^{n} f\\left(a + r \\cdot \\dfrac{b-a}{n}\\right) \\cdot \\dfrac{b-a}{n}$.',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'In JEE problems, recognize a limit of the form $\\lim_{n \\to \\infty} \\dfrac{1}{n} \\sum_{r=1}^{n} f(r/n)$ as $\\int_0^1 f(x)\\,dx$.',
        hindi: 'जेईई समस्याओं में, फॉर्म $\\lim_{n \\to \\infty} \\dfrac{1}{n} \\sum_{r=1}^{n} f(r/n)$ की सीमा को $\\int_0^1 f(x)\\,dx$ के रूप में पहचानें।',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Evaluate $\\displaystyle \\lim_{n \\to \\infty} \\dfrac{1}{n} \\sum_{r = 1}^{n} \\sin\\left(\\dfrac{r\\pi}{n}\\right)$.',
        hindi: '$\\displaystyle \\lim_{n \\to \\infty} \\dfrac{1}{n} \\sum_{r = 1}^{n} \\sin\\left(\\dfrac{r\\pi}{n}\\right)$ का मूल्यांकन करें.',
          },
          solution: {
            english: '$= \\int_0^1 \\sin(\\pi x)\\,dx = \\left[-\\dfrac{\\cos \\pi x}{\\pi}\\right]_0^1 = \\dfrac{2}{\\pi}$.',
        hindi: '$= \\int_0^1 \\sin(\\pi x)\\,dx = \\left[-\\dfrac{\\cos \\pi x}{\\pi}\\right]_0^1 = \\dfrac{2}{\\pi}$.',
          },
        },
      ],
    },
    {
      id: 'area-curves',
      title: {
        english: 'Area Under and Between Curves',
        hindi: 'वक्रों के नीचे और बीच का क्षेत्रफल',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Area under curve',
        hindi: 'वक्र के अंतर्गत क्षेत्र',
          },
          statement: {
            english: `• Area between $y = f(x)$ and $x$-axis on $[a, b]$ (with $f \\ge 0$): $A = \\int_a^b f(x)\\,dx$.
• If $f$ changes sign, split the integral and add absolute values.
• Area to the left of $x = g(y)$, $y$-axis on $[c, d]$: $A = \\int_c^d g(y)\\,dy$.`,
            hindi: '• $[a, b]$ पर $y = f(x)$ और $x$-अक्ष के बीच का क्षेत्र ($f \\ge 0$ के साथ): $A = \\int_a^b f(x)\\,dx$। • यदि $f$ चिह्न बदलता है, तो अभिन्न को विभाजित करें और निरपेक्ष मान जोड़ें। • $x = g(y)$ के बाईं ओर का क्षेत्र, $y$-अक्ष पर $[c, d]$: $A = \\int_c^d g(y)\\,dy$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Area between two curves',
        hindi: 'दो वक्रों के बीच का क्षेत्र',
          },
          statement: {
            english: `If $f(x) \\ge g(x)$ on $[a, b]$, area enclosed is
$$A = \\int_a^b [f(x) - g(x)]\\,dx.$$
Intersection points $a, b$ are obtained by solving $f(x) = g(x)$.`,
            hindi: 'यदि $[a, b]$ पर $f(x) \\ge g(x)$ है, तो संलग्न क्षेत्र है $$A = \\int_a^b [f(x) - g(x)]\\,dx.$$ प्रतिच्छेदन बिंदु $a, b$ $f(x) = g(x)$ को हल करके प्राप्त किए जाते हैं।',
          },
        },
        {
          type: 'example',
          number: 5,
          problem: {
            english: 'Find area enclosed by $y = x^2$ and $y = 2x$.',
        hindi: '$y = x^2$ और $y = 2x$ से घिरा क्षेत्र खोजें।',
          },
          solution: {
            english: 'Intersection: $x^2 = 2x \\Rightarrow x = 0, 2$. On $(0, 2)$: $2x \\ge x^2$. $A = \\int_0^2 (2x - x^2)\\,dx = [x^2 - x^3/3]_0^2 = 4 - 8/3 = 4/3$.',
        hindi: 'प्रतिच्छेदन: $x^2 = 2x \\Rightarrow x = 0, 2$। $(0, 2)$ पर: $2x \\ge x^2$. $A = \\int_0^2 (2x - x^2)\\,dx = [x^2 - x^3/3]_0^2 = 4 - 8/3 = 4/3$.',
          },
        },
        {
          type: 'example',
          number: 6,
          problem: {
            english: 'Area enclosed between parabola $y^2 = 4x$ and line $y = 2x - 4$.',
        hindi: 'परवलय $y^2 = 4x$ और रेखा $y = 2x - 4$ के बीच घिरा क्षेत्र।',
          },
          solution: {
            english: 'Intersection: $y^2 = 4 \\cdot \\dfrac{y + 4}{2} \\Rightarrow y^2 - 2y - 8 = 0 \\Rightarrow y = 4, -2$. Integrating w.r.t. $y$ (right curve $-$ left curve): $A = \\int_{-2}^{4} \\left[\\dfrac{y+4}{2} - \\dfrac{y^2}{4}\\right]\\,dy = 9$.',
        hindi: 'प्रतिच्छेदन: $y^2 = 4 \\cdot \\dfrac{y + 4}{2} \\Rightarrow y^2 - 2y - 8 = 0 \\Rightarrow y = 4, -2$. w.r.t. को एकीकृत करना $y$ (दायां वक्र $-$ बायां वक्र): $A = \\int_{-2}^{4} \\left[\\dfrac{y+4}{2} - \\dfrac{y^2}{4}\\right]\\,dy = 9$.',
          },
        },
      ],
    },
    {
      id: 'mixed-tricks',
      title: {
        english: 'Useful Tricks for Definite Integrals',
        hindi: 'निश्चित समाकलों के लिए उपयोगी युक्तियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Differentiation under the integral sign** (Feynman trick): introduce a parameter $\\alpha$ and differentiate $I(\\alpha)$ w.r.t. $\\alpha$.',
        hindi: '**अभिन्न चिह्न के अंतर्गत विभेदन** (फेनमैन ट्रिक): एक पैरामीटर $\\alpha$ प्रस्तुत करें और $I(\\alpha)$ w.r.t. में अंतर करें। $\\alpha$.',
            },
            {
              english: '**Symmetry**: split $\\int_{-a}^{a}$ into even/odd parts.',
        hindi: '**समरूपता**: $\\int_{-a}^{a}$ को even/odd भागों में विभाजित करें।',
            },
            {
              english: '**Substitution**: $x \\to a - x$, $x \\to 1/x$, etc., often produce a relation involving the original integral.',
        hindi: '**प्रतिस्थापन**: $x \\to a - x$, $x \\to 1/x$, आदि, अक्सर मूल अभिन्न अंग को शामिल करते हुए एक संबंध उत्पन्न करते हैं।',
            },
            {
              english: '**Comparison with known integrals** like $\\int_0^\\infty e^{-x^2}\\,dx = \\sqrt\\pi/2$ (Gauss).',
        hindi: '**ज्ञात इंटीग्रल्स के साथ तुलना** जैसे $\\int_0^\\infty e^{-x^2}\\,dx = \\sqrt\\pi/2$ (गॉस)।',
            },
          ],
        },
        {
          type: 'example',
          number: 7,
          problem: {
            english: 'Evaluate $\\displaystyle I = \\int_0^{\\pi} x f(\\sin x)\\,dx$ in terms of $\\int_0^{\\pi} f(\\sin x)\\,dx$.',
        hindi: '$\\int_0^{\\pi} f(\\sin x)\\,dx$ के संदर्भ में $\\displaystyle I = \\int_0^{\\pi} x f(\\sin x)\\,dx$ का मूल्यांकन करें।',
          },
          solution: {
            english: 'King: $I = \\int_0^{\\pi} (\\pi - x) f(\\sin(\\pi - x))\\,dx = \\pi \\int_0^{\\pi} f(\\sin x)\\,dx - I$. So $I = \\dfrac{\\pi}{2} \\int_0^{\\pi} f(\\sin x)\\,dx$.',
        hindi: 'राजा: $I = \\int_0^{\\pi} (\\pi - x) f(\\sin(\\pi - x))\\,dx = \\pi \\int_0^{\\pi} f(\\sin x)\\,dx - I$. तो $I = \\dfrac{\\pi}{2} \\int_0^{\\pi} f(\\sin x)\\,dx$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\int_a^b f = F(b) - F(a)',
      desc: {
        english: 'FTC Part 2',
        hindi: 'एफटीसी भाग 2',
      },
    },
    {
      latex: '\\tfrac{d}{dx}\\int_a^x f(t)\\,dt = f(x)',
      desc: {
        english: 'FTC Part 1',
        hindi: 'एफटीसी भाग 1',
      },
    },
    {
      latex: '\\int_a^b f(x)\\,dx = \\int_a^b f(a+b-x)\\,dx',
      desc: {
        english: 'King\'s rule',
        hindi: 'राजा का शासन',
      },
    },
    {
      latex: '\\int_{-a}^{a} f = 0\\ (f\\ \\text{odd})',
      desc: {
        english: 'Symmetry',
        hindi: 'समरूपता',
      },
    },
    {
      latex: '\\int_0^{2a} f = 2\\int_0^a f\\ \\text{if}\\ f(2a-x) = f(x)',
      desc: {
        english: '',
        hindi: '0 2a f = 2 0 a f\\ if\\ f(2a-x) = f(x)',
      },
    },
    {
      latex: '\\lim \\tfrac{1}{n}\\sum f(r/n) = \\int_0^1 f',
      desc: {
        english: 'Riemann sum',
        hindi: 'रीमैन योग',
      },
    },
    {
      latex: 'A = \\int_a^b [f(x) - g(x)]\\,dx',
      desc: {
        english: 'Area between curves',
        hindi: 'वक्रों के बीच का क्षेत्र',
      },
    },
    {
      latex: '\\int_0^{\\pi/2} \\sin^n x\\,dx = \\text{Wallis}',
      desc: {
        english: '',
        hindi: '0/2 n x\\,dx = वालिस',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'King\'s rule',
      problem: {
        english: '$\\displaystyle \\int_0^{\\pi/2} \\dfrac{dx}{1 + \\tan x} = ?$',
        hindi: '$\\displaystyle \\int_0^{\\pi/2} \\dfrac{dx}{1 + \\tan x} =?$',
      },
      answer: {
        english: '$\\pi/4$',
        hindi: '$\\pi/4$',
      },
      solution: {
        english: 'Same trick as Example 1: add $I$ to its king-transform to get $2I = \\pi/2$.',
        hindi: 'उदाहरण 1 जैसी ही ट्रिक: $2I = \\pi/2$ प्राप्त करने के लिए इसके किंग-ट्रांसफॉर्म में $I$ जोड़ें।',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Wallis',
      problem: {
        english: '$\\displaystyle \\int_0^{\\pi/2} \\cos^4 x\\,dx = ?$',
        hindi: '$\\displaystyle \\int_0^{\\pi/2} \\cos^4 x\\,dx =?$',
      },
      answer: {
        english: '$\\dfrac{3\\pi}{16}$',
        hindi: '$\\dfrac{3\\pi}{16}$',
      },
      solution: {
        english: 'Wallis: $\\dfrac{3 \\cdot 1}{4 \\cdot 2} \\cdot \\dfrac{\\pi}{2} = \\dfrac{3\\pi}{16}$.',
        hindi: 'वालिस: $\\dfrac{3 \\cdot 1}{4 \\cdot 2} \\cdot \\dfrac{\\pi}{2} = \\dfrac{3\\pi}{16}$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Riemann',
      problem: {
        english: 'Evaluate $\\displaystyle \\lim_{n \\to \\infty} \\sum_{r = 1}^{n} \\dfrac{n}{n^2 + r^2}$.',
        hindi: '$\\displaystyle \\lim_{n \\to \\infty} \\sum_{r = 1}^{n} \\dfrac{n}{n^2 + r^2}$ का मूल्यांकन करें.',
      },
      answer: {
        english: '$\\pi/4$',
        hindi: '$\\pi/4$',
      },
      solution: {
        english: '$= \\lim \\dfrac{1}{n} \\sum \\dfrac{1}{1 + (r/n)^2} = \\int_0^1 \\dfrac{dx}{1 + x^2} = \\tan^{-1} 1 = \\pi/4$.',
        hindi: '$= \\lim \\dfrac{1}{n} \\sum \\dfrac{1}{1 + (r/n)^2} = \\int_0^1 \\dfrac{dx}{1 + x^2} = \\tan^{-1} 1 = \\pi/4$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Area',
      problem: {
        english: 'Find area bounded by $y = x^2$, $y$-axis, and $y = 4$.',
        hindi: '$y = x^2$, $y$-अक्ष, और $y = 4$ से घिरा क्षेत्र खोजें।',
      },
      answer: {
        english: '$32/3$',
        hindi: '$32/3$',
      },
      solution: {
        english: '$A = \\int_0^4 \\sqrt y\\,dy \\cdot 2$ (symmetry about $y$-axis) $= 2 \\cdot \\dfrac{2}{3}[y^{3/2}]_0^4 = \\dfrac{32}{3}$.',
        hindi: '$A = \\int_0^4 \\sqrt y\\,dy \\cdot 2$ ($y$-अक्ष के बारे में समरूपता) $= 2 \\cdot \\dfrac{2}{3}[y^{3/2}]_0^4 = \\dfrac{32}{3}$.',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Properties',
      problem: {
        english: 'Evaluate $\\displaystyle \\int_{-1}^{1} \\dfrac{x^4 (1 - x)^4}{1 + x^2}\\,dx$.',
        hindi: '$\\displaystyle \\int_{-1}^{1} \\dfrac{x^4 (1 - x)^4}{1 + x^2}\\,dx$ का मूल्यांकन करें।',
      },
      answer: {
        english: 'Use even part of the integrand and reduce. Final value $= \\dfrac{22}{7} - \\pi$.',
        hindi: 'इंटीग्रैंड के सम भाग का उपयोग करें और कम करें। अंतिम मान $= \\dfrac{22}{7} - \\pi$.',
      },
      solution: {
        english: 'Classical Putnam-style integral; expansion of numerator with polynomial division yields the famous result.',
        hindi: 'शास्त्रीय पटनम-शैली अभिन्न; बहुपद विभाजन के साथ अंश का विस्तार प्रसिद्ध परिणाम देता है। **भाग 1**: यदि और पर निरंतर है, तो । **भाग 2**: यदि पर निरंतर है और का कोई प्रतिव्युत्पन्न है, तो',
      },
    },
  ],
  next: {
    slug: 'differential-equations',
    title: 'Differential Equations',
  },
};

export default chapter;
